import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleFilled
} from "@ant-design/icons";
import {
  ConfigProvider,
  Button,
  Modal,
  Table,
  Card,
  Tag,
  Space,
  Tooltip,
  Statistic,
  Row,
  Col,
  Form,
  Input,
  DatePicker
} from "antd";
import frFR from "antd/locale/fr_FR";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { confirm } = Modal;

const Exercices = () => {
  const [exercices, setExercices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedExercice, setSelectedExercice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchExercices();
  }, []);

  const fetchExercices = () => {
    setLoading(true);
    axiosInstance
      .get(`/api/exercice`)
      .then((res) => {
        setExercices(res.data);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des exercices");
      })
      .finally(() => setLoading(false));
  };

  const handleShow = (edit, exercice = null) => {
    setEditMode(edit);
    setSelectedExercice(exercice);

    if (edit && exercice) {
      form.setFieldsValue({
        libelle: exercice.libelle,
        annee: exercice.annee ? dayjs(exercice.annee) : null,
        periode: exercice.dateDebut && exercice.dateFin
          ? [dayjs(exercice.dateDebut), dayjs(exercice.dateFin)]
          : null,
        commentaire: exercice.commentaire
      });
    } else {
      form.resetFields();
    }

    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedExercice(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setSaving(true);

    try {
      const exerciceData = {
        exerciceId: selectedExercice?.exerciceId,
        libelleExercice: values.libelle.trim(),
        annee: values.annee.format('YYYY-01-01'),
        dateDebut: values.periode[0].format('YYYY-MM-DD'),
        dateFin: values.periode[1].format('YYYY-MM-DD'),
        commentaire: values.commentaire?.trim() || null
      };

      console.log("Données à enregistrer:", exerciceData);
      const request = editMode
        ? axiosInstance.put(`/api/exercice`, exerciceData)
        : axiosInstance.post(`/api/exercice`, exerciceData);

      const response = await request;

      toast.success(editMode ? "Exercice modifié avec succès" : "Exercice créé avec succès");

      if (editMode) {
        setExercices(prev =>
          prev.map(ex => ex.exerciceId === response.data.exerciceId ? response.data : ex)
        );
      } else {
        setExercices(prev => [response.data, ...prev]);
      }

      handleClose();
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (exercice) => {
    confirm({
      title: "Confirmer la suppression",
      icon: <ExclamationCircleFilled />,
      content: `Êtes-vous sûr de vouloir supprimer l'exercice "${exercice.libelle}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => {
        axiosInstance
          .delete(`/api/exercice/${exercice.exerciceId}`)
          .then(() => {
            setExercices(prev => prev.filter(ex => ex.exerciceId !== exercice.exerciceId));
            toast.success("Exercice supprimé avec succès");
          })
          .catch((err) => {
            console.error("Erreur lors de la suppression:", err);
            toast.error("Erreur lors de la suppression");
          });
      },
    });
  };

  const isExerciceActif = (exercice) => {
    if (!exercice.dateDebut || !exercice.dateFin) return false;

    const maintenant = dayjs();
    const debut = dayjs(exercice.dateDebut);
    const fin = dayjs(exercice.dateFin);

    // Vérifier si maintenant est après ou égal à debut ET avant ou égal à fin
    return (maintenant.isAfter(debut) || maintenant.isSame(debut, 'day')) &&
      (maintenant.isBefore(fin) || maintenant.isSame(fin, 'day'));
  };

  const calculerStatistiques = () => {
    const total = exercices.length;
    const actifs = exercices.filter(isExerciceActif).length;
    const anneeEnCours = new Date().getFullYear();
    const exercicesAnneeEnCours = exercices.filter(
      ex => ex.annee && new Date(ex.annee).getFullYear() === anneeEnCours
    ).length;

    return { total, actifs, exercicesAnneeEnCours };
  };

  const columns = [
    {
      title: 'Libellé',
      dataIndex: 'libelle',
      key: 'libelle',
      render: (libelle, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: '15px' }}>{record.commentaire}</div>
          {/* {record.commentaire && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {record.commentaire}
            </div>
          )} */}
        </div>
      ),
    },
    {
      title: 'Année',
      dataIndex: 'annee',
      key: 'annee',
      render: (annee) => (
        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
          {annee ? new Date(annee).getFullYear() : 'N/A'}
        </Tag>
      ),
      sorter: (a, b) => new Date(a.annee) - new Date(b.annee),
      width: 120,
    },
    {
      title: 'Période de validité',
      key: 'periode',
      render: (_, record) => (
        <div>
          {record.dateDebut && record.dateFin ? (
            <>
              <div style={{ fontSize: '13px' }}>
                <CalendarOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                <strong>Début:</strong> {dayjs(record.dateDebut).format('DD/MM/YYYY')}
              </div>
              <div style={{ fontSize: '13px', marginTop: 4 }}>
                <CalendarOutlined style={{ marginRight: 6, color: '#ff4d4f' }} />
                <strong>Fin:</strong> {dayjs(record.dateFin).format('DD/MM/YYYY')}
              </div>
            </>
          ) : (
            <span style={{ color: '#999' }}>Non définie</span>
          )}
        </div>
      ),
      width: 220,
    },
    {
      title: 'Durée',
      key: 'duree',
      render: (_, record) => {
        if (!record.dateDebut || !record.dateFin) return '-';

        const debut = dayjs(record.dateDebut);
        const fin = dayjs(record.dateFin);
        const mois = fin.diff(debut, 'month');
        const jours = fin.diff(debut, 'day');

        return (
          <div style={{ fontSize: '13px' }}>
            <div>{mois + 1} mois</div>
            <div style={{ color: '#666' }}>({jours + 1} jours)</div>
          </div>
        );
      },
      align: 'center',
      width: 120,
    },
    {
      title: 'Statut',
      key: 'statut',
      render: (_, record) => {
        const actif = isExerciceActif(record);
        return actif ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Actif
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            Inactif
          </Tag>
        );
      },
      filters: [
        { text: 'Actif', value: 'actif' },
        { text: 'Inactif', value: 'inactif' },
      ],
      onFilter: (value, record) => {
        const actif = isExerciceActif(record);
        return value === 'actif' ? actif : !actif;
      },
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleShow(true, record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
      align: 'center',
      fixed: 'right',
      width: 120,
    },
  ];

  const stats = calculerStatistiques();

  return (
    <div className="layout-wrapper layout-content-navbar">
      <style>{`
        .exercices-header-card {
          background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(24, 144, 255, 0.25);
          color: white;
        }

        .exercices-header-card h2 {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .stats-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }
      `}</style>

      <div className="layout-container">
        <SideBar currentMenu={"AUTRES_PARAMETRES"} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper" style={{ background: '#f5f5f5' }}>
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* En-tête */}
              <div className="exercices-header-card">
                <h2>
                  <CalendarOutlined /> Gestion des Exercices
                </h2>
                <p>Configuration des exercices comptables avec périodes de validité</p>
              </div>

              {/* Statistiques */}
              <div className="stats-card">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total Exercices"
                      value={stats.total}
                      prefix={<CalendarOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Exercices Actifs"
                      value={stats.actifs}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Année en cours"
                      value={stats.exercicesAnneeEnCours}
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                </Row>
              </div>

              {/* Actions */}
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleShow(false)}
                    size="large"
                  >
                    Nouvel exercice
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchExercices}
                    loading={loading}
                  >
                    Actualiser
                  </Button>
                </Space>
              </div>

              {/* Table */}
              <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
                <ConfigProvider locale={frFR}>
                  <Table
                    columns={columns}
                    dataSource={exercices}
                    loading={loading}
                    rowKey="exerciceId"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total: ${total} exercice(s)`,
                    }}
                    scroll={{ x: 1000 }}
                  />
                </ConfigProvider>
              </Card>

            </div>
            <Footer />
          </div>
        </div>
      </div>

      {/* Modal de création/édition */}
      <Modal
        title={
          <span>
            <CalendarOutlined style={{ marginRight: 8 }} />
            {editMode ? 'Modifier l\'exercice' : 'Nouvel exercice'}
          </span>
        }
        open={showModal}
        onCancel={handleClose}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Libellé de l'exercice"
            name="libelle"
            rules={[{ required: true, message: 'Veuillez saisir le libellé' }]}
          >
            <Input
              placeholder="Ex: Exercice fiscal 2024"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Année de référence"
            name="annee"
            rules={[{ required: true, message: 'Veuillez sélectionner l\'année' }]}
          >
            <DatePicker
              picker="year"
              placeholder="Sélectionner l'année"
              style={{ width: '100%' }}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Période de validité"
            name="periode"
            rules={[{ required: true, message: 'Veuillez sélectionner la période' }]}
          >
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Date de début', 'Date de fin']}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Commentaire"
            name="commentaire"
          >
            <Input.TextArea
              rows={3}
              placeholder="Commentaire optionnel"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleClose}>
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
              >
                {editMode ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default Exercices;